<template>
	<VerticalLayout>
		<div>
			<TypographyTitle :level="3">{{ $t('settings.sub-networks.subNetworkFilter.label') }}</TypographyTitle>
			<Card :bordered="false">
				<HorizontalLayout size="middle" :direction="buttonsDirection">
					<TypographyText v-if="statusData.maxSubNetworksCount === 1" strong>{{ subNetworkOptions[0].label }}</TypographyText>
					<Select
						v-else
						class="ui-select"
						v-model:value="subNetworkSelectedIdTemp"
						:options="subNetworkOptions"
						option-label-prop="labelTitle"
						@change="onSubNetworkChange"
					>
						<template #option="{ label, title, deleted }">
							<TypographyText strong>{{ label }}</TypographyText> | {{ title }}
							<TypographyText type="secondary" v-if="deleted">{{ $t('settings.sub-networks.deleted') }}</TypographyText>
						</template>
					</Select>
					<HorizontalLayout>
						<Button
							v-if="statusData.maxSubNetworksCount !== 1"
							class="width-100"
							type="primary"
							@click="onAddSubNetworkClick"
							:disabled="!statusData.allowAddSubNetwork"
						>
							<PlusCircleOutlined />{{ $t('settings.sub-networks.addButtonLabel') }}
						</Button>
						<Button class="width-100" @click="onRenameSubNetworkClick" :disabled="!allowRenameSubNetwork">
							{{ $t('settings.sub-networks.renameButtonLabel') }}
						</Button>
						<PopconfirmButton
							v-if="statusData.maxSubNetworksCount !== 1"
							:title="$t('settings.sub-networks.deleteConfirmation')"
							:ok-text="$t('settings.sub-networks.deleteButtonLabel')"
							:cancel-text="$t('settings.sub-networks.cancelButtonLabel')"
							:disabled="!allowDeleteSubNetwork"
							:onConfirm="onDeleteSubNetworkConfirm"
							buttonClass="width-100"
						>
							{{ $t('settings.sub-networks.deleteButtonLabel') }}
						</PopconfirmButton>
					</HorizontalLayout>
				</HorizontalLayout>
			</Card>
		</div>
		<MasonryContainer>
			<MasonryItem data-testid="settings-subnetwork-settings-card" headerKey="settings.vrsSettingsForm.subNetworkSettingsHeader">
				<SubNetworksSimpleSettingsForm
					:loading="isLoading"
					:settings="snSettings"
					:cnetData="snCnetData"
					:onUpdateField="onUpdateField"
					:storagePath="storagePath"
				/>
			</MasonryItem>
			<MasonryItem data-testid="settings-subnetwork-base-stations-card" headerKey="settings.sub-networks.vrsSettingsForm.baseStationsDivider">
				<SubNetworksBaseStationsForm
					:loading="isLoading"
					:settings="snSettings"
					:onUpdateField="onUpdateField"
					:fetchSettings="fetchSettings"
				/>
			</MasonryItem>
			<MasonryItem data-testid="settings-subnetwork-additional-card" headerKey="settings.sub-networks.vrsSettingsForm.otherSettingsDivider">
				<SubNetworksOtherSettingsForm
					:loading="isLoading"
					:settings="snSettings"
					:fetchSettings="fetchSettings"
					:onUpdateField="onUpdateField"
				/>
			</MasonryItem>
			<MasonryItem v-if="licenseAllowGp" data-testid="settings-subnetwork-ppp-rtk-card" headerKey="settings.vrsSettingsForm.pppRtkHeader">
				<PppRtkSettingsForm
					:loading="isLoading"
					:settings="snSettings"
					:cnetData="snCnetData"
					:onUpdateField="onUpdateField"
					:fetchSettings="fetchSettings"
				/>
			</MasonryItem>
			<MasonryItem data-testid="settings-subnetwork-active-signals-card" headerKey="settings.vrsSettingsForm.activeSignalsHeader">
				<ActiveSignalsForm
					:loading="isLoading"
					:status="statusData"
					:settings="snSettings"
					:onUpdateField="onUpdateField"
				/>
			</MasonryItem>
			<MasonryItem  v-if="licenseAllowVrs" data-testid="settings-subnetwork-vrs-mountpoints-card" headerKey="settings.vrsSettingsForm.rtcmMessagesFiltersVRSHeader">
				<SettingsInfoBanner v-if="showVrsMountpointsHint" class="pd-md-vertical">
					{{ $t('settings.sub-networks.vrsSettingsForm.vrsMountpointsDisabledHint') }}
				</SettingsInfoBanner>
				<RtcmMessagesFiltersGroup
					:loading="isLoading"
					:status="statusData"
					:settings="snSettings"
					:fetchSettings="fetchSettings"
					:isVrs="true"
					:disabled="vrsMountpointsDisabled"
				/>
			</MasonryItem>
			<MasonryItem v-if="licenseAllowPpp" data-testid="settings-subnetwork-cssr-mountpoints-card" headerKey="settings.vrsSettingsForm.cssrMessagesFiltersVRSHeader">
				<SettingsInfoBanner v-if="showCssrMountpointsHint" class="pd-md-vertical">
					{{ $t('settings.sub-networks.vrsSettingsForm.cssrMountpointsDisabledHint') }}
				</SettingsInfoBanner>
				<CssrMessagesFiltersGroup
					:loading="isLoading"
					:status="statusData"
					:settings="snSettings"
					:fetchSettings="fetchSettings"
					:disabled="cssrMountpointsDisabled"
				/>
			</MasonryItem>
		</MasonryContainer>
	</VerticalLayout>

	<SubNetworkAddRenameModal
		:addMode="addMode"
		:subNetworkId="subNetworkSelectedId"
		:subNetworkLabel="selectedSubNetworkLabel"
		:subNetworkTitle="selectedSubNetworkTitle"
		:baseMountpoint="vrsName"
		v-model="addRenamePopupVisible"
		:onSuccess="onAddRenameSubNetworkDone"
		:fetchSettings="fetchSettings"
	/>

	<RouteLeaveModal
		:visible="modalVisible"
		:confirm="modalOk"
		:cancel="modalCancel"
	/>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { TypographyTitle, Select, Card, Button, TypographyText, message } from 'ant-design-vue';
import { PlusCircleOutlined } from '@ant-design/icons-vue';

import { useI18n } from 'vue-i18n'

import { useRoute, useRouter } from 'vue-router';

import Api from '@/api/Api';
import { toCamelCase } from '@/api/ApiHelpers';

import { showErrorMessage } from '@/components/common/errorMessage';

import SubNetworksSimpleSettingsForm from '@/components/settings/subNetworks/SubNetworksSimpleSettingsForm.vue';
import SubNetworksBaseStationsForm from '@/components/settings/subNetworks/SubNetworksBaseStationsForm.vue';
import SubNetworksOtherSettingsForm from '@/components/settings/subNetworks/SubNetworksOtherSettingsForm.vue';
import PppRtkSettingsForm from '@/components/settings/subNetworks/PppRtkSettingsForm.vue';
import ActiveSignalsForm from '@/components/settings/subNetworks/activeSignalsForm/ActiveSignalsForm.vue';
import RtcmMessagesFiltersGroup from '@/components/settings/rtcmFilters/RtcmMessagesFiltersGroup.vue';
import CssrMessagesFiltersGroup from '@/components/settings/cssrFilters/CssrMessagesFiltersGroup.vue';
import SubNetworkAddRenameModal from '@/components/settings/subNetworks/SubNetworkAddRenameModal.vue';

import MasonryContainer from '@/components/masonry/MasonryContainer.vue';
import MasonryItem from '@/components/masonry/MasonryItem.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';
import HorizontalLayout from '@/components/common/HorizontalLayout.vue';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import SettingsInfoBanner from '@/components/common/SettingsInfoBanner.vue';

import RouteLeaveModal from '@/components/common/RouteLeaveModal.vue';
import { useRouteLeaveModalManual } from '@/components/common/useRouteLeaveModalManual';

import { useLicenseStore } from '@/stores/licenseStore';

import { useSmallScreen } from '@/utils/useSmallScreen';
import { useProvideInjectFormChangedStatuses } from '@/utils/useProvideInject';

export default {
	components: {
		TypographyTitle, Select, Card, Button, TypographyText,
		SettingsInfoBanner,
		PlusCircleOutlined,
		MasonryContainer, MasonryItem,
		SubNetworksSimpleSettingsForm, SubNetworksBaseStationsForm,
		SubNetworksOtherSettingsForm, PppRtkSettingsForm, ActiveSignalsForm, RtcmMessagesFiltersGroup, CssrMessagesFiltersGroup,
		VerticalLayout, RouteLeaveModal, SubNetworkAddRenameModal, PopconfirmButton,
		HorizontalLayout,
	},
	setup() {
		const { t } = useI18n();
		const isLoading = ref(true);
		const statusData = ref({});
		const subNetworksSettings = ref([]);
		const storagePath = ref('');

		// два id нужно для реализации проверки изменений в форме
		// Select меняет Temp. При его изменении мы проверяем несохраненные изменения в формах
		// и если их нет, то меняем основной id
		// если есть, выдаем модалку
		// при нажатии на кнопку "Ок" меняем основной id на Temp
		// при нажатии на кнопку "Отмена" меняем Temp назад на основной id
		// только при смене основного id происходит запрос на сервер
		const subNetworkSelectedId = ref(null);
		const subNetworkSelectedIdTemp = ref(null);
		const subNetworkOptions = computed(() => subNetworksSettings.value.map(item => ({
			value: item.settings.id,
			label: item.settings.label,
			title: item.settings.title,
			deleted: item.settings.deleted,
			labelTitle: item.settings.label + ' | ' + item.settings.title + (item.settings.deleted ? ' ' + t('settings.sub-networks.deleted') : ''),
		})));

		const snSettings = ref({});
		const snCnetData = ref({});
		const updateSelectedSubNetworkData = () => {
			const result = subNetworksSettings.value.find(item => item.settings.id === subNetworkSelectedId.value);
			snSettings.value = (result != null) ? toCamelCase(result.settings) : {};
			snCnetData.value = (result != null) ? result.cnetData : {};
		};

		const allowRenameSubNetwork = computed(
			() => subNetworksSettings.value.some(item => item.settings.id === subNetworkSelectedId.value && !item.settings.deleted),
		);

		const allowDeleteSubNetwork = computed(
			() => allowRenameSubNetwork.value &&subNetworksSettings.value.filter(item => !item.settings.deleted).length > 1,
		);

		const selectedSubNetworkLabel = computed(() => {
			const result = subNetworksSettings.value.find(item => item.settings.id === subNetworkSelectedId.value);
			return result ? result.settings.label : '';
		});

		const selectedSubNetworkTitle = computed(() => {
			const result = subNetworksSettings.value.find(item => item.settings.id === subNetworkSelectedId.value);
			return result ? result.settings.title : '';
		});

		watch(subNetworkSelectedId, () => updateSelectedSubNetworkData());

		const route = useRoute();
		const router = useRouter();
		const gotoSubNetwork = id => {
			router.push({
				name: 'settings-sub-network-edit',
				params: { id: route.params.id, subnetwork_id: id },
			});
		};
		const licenseStore = useLicenseStore();
		const licenseAllowVrs = computed(() => licenseStore.allowVrs);
		const licenseAllowPpp = computed(() => licenseStore.allowPppRtk);
		const licenseAllowGp = computed(() => licenseStore.allowPppGp);
		const subNetworkSettingsLoaded = computed(() => snSettings.value.id != null);
		const vrsMountpointsDisabled = computed(() => (
			!subNetworkSettingsLoaded.value || !snSettings.value.ivrsEnabled
		));
		const showVrsMountpointsHint = computed(() => (
			subNetworkSettingsLoaded.value && !snSettings.value.ivrsEnabled
		));
		const cssrMountpointsDisabled = computed(() => (
			!subNetworkSettingsLoaded.value || !snSettings.value.cssrBrdcEnabled
		));
		const showCssrMountpointsHint = computed(() => (
			subNetworkSettingsLoaded.value && !snSettings.value.cssrBrdcEnabled
		));
		const vrsName = ref('');
		const fetchSettings = (callback, hideLoading) => {
			if (!hideLoading) {
				isLoading.value = true;
			}
			Api.vrsSettingsGet(responseData => {
				vrsName.value = responseData.settings.name_vrs;
				statusData.value = responseData.statusData;
				storagePath.value = responseData.storagePath || '';
				subNetworksSettings.value = responseData.subNetworksSettings;
				if (subNetworkSelectedId.value == null && subNetworksSettings.value.length > 0) {
					const routeSubnetworkId = parseInt(route.params?.subnetwork_id);
					const found = subNetworksSettings.value.find(item => item.settings.id === routeSubnetworkId);
					subNetworkSelectedId.value = found != null ? routeSubnetworkId : subNetworksSettings.value[0].settings.id;
				}
				subNetworkSelectedIdTemp.value = subNetworkSelectedId.value;
				updateSelectedSubNetworkData();
				isLoading.value = false;
				if (callback) {
					callback(responseData);
				}
			});
		};

		const onUpdateField = (field, value, callback) => Api.subNetworkSettingsFieldPost(
			subNetworkSelectedId.value,
			field,
			value,
			response => {
				if (response.status === 'ok') {
					// не показываем лоадер, если все ок
					fetchSettings(() => {}, true);
				} else {
					showErrorMessage(response.errors);
					// показываем лоадер, если ошибка
					fetchSettings();
				}
				if (callback) {
					callback(response);
				}
			},
		);

		const { injectValue } = useProvideInjectFormChangedStatuses;
		const {
			modalVisible,
			modalOk,
			modalCancel,
			checkFormsChanged,
		} = useRouteLeaveModalManual(
			injectValue(),
			() => { gotoSubNetwork(subNetworkSelectedIdTemp.value); },
			() => { subNetworkSelectedIdTemp.value = subNetworkSelectedId.value; },
		);

		const onSubNetworkChange = value => {
			if (!checkFormsChanged()) {
				gotoSubNetwork(value);
			}
		};
		const addRenamePopupVisible = ref(false);
		const addMode = ref(true);
		
		const onAddSubNetworkClick = () => {
			if (checkFormsChanged()) {
				modalVisible.value = true;
			} else {
				addMode.value = true;
				addRenamePopupVisible.value = true;
			}
		};
		
		const onRenameSubNetworkClick = () => {
			if (checkFormsChanged()) {
				modalVisible.value = true;
			} else {
				addMode.value = false;
				addRenamePopupVisible.value = true;
			}
		};
		
		const onAddRenameSubNetworkDone = id => { gotoSubNetwork(id); };

		const onDeleteSubNetworkConfirm = () => Api.subNetworkDelete(subNetworkSelectedId.value, response => {
			if (response.status === 'ok') {
				message.success(t('settings.sub-networks.deleteSuccess'));
				fetchSettings();
			} else {
				showErrorMessage(response.errors);
			}
		});
		onMounted(fetchSettings);
		
		const { smallScreen } = useSmallScreen(780);
		const buttonsDirection = computed(() => smallScreen.value ? 'vertical' : 'horizontal');
		return {
			subNetworksSettings,
			subNetworkSelectedId,
			subNetworkSelectedIdTemp,
			subNetworkOptions,
			storagePath,
			onSubNetworkChange,

			addRenamePopupVisible,
			addMode,
			vrsName,
			onAddSubNetworkClick,
			onRenameSubNetworkClick,
			onAddRenameSubNetworkDone,

			allowDeleteSubNetwork,
			allowRenameSubNetwork,
			selectedSubNetworkLabel,
			selectedSubNetworkTitle,
			onDeleteSubNetworkConfirm,

			snSettings,
			snCnetData,

			isLoading,
			statusData,
			licenseAllowVrs,
			licenseAllowPpp,
			licenseAllowGp,
			vrsMountpointsDisabled,
			showVrsMountpointsHint,
			cssrMountpointsDisabled,
			showCssrMountpointsHint,
			fetchSettings,
			onUpdateField,

			modalVisible,
			modalOk,
			modalCancel,

			buttonsDirection,
		};
	},
}
</script>

<style scoped>
.ui-select {
	width: clamp(170px, 30vw, 400px);
}
@media (max-width: 900px) {
	.ui-select {
		width: calc(100vw - 190px);
	}
}
</style>
